import React from 'react';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Procurement } from '@/types/database.types';

interface QualityInfoBadgeProps {
  procurement: Procurement;
  compact?: boolean;
}

interface QualityData {
  grading_type?: 'preliminary' | 'final' | string | null;
  compensation_kg?: number | null;
  preliminary_compensation_kg?: number | null;
  damage_kg?: number | null;
  preliminary_damage_kg?: number | null;
  damage_percentage?: number | null;
  preliminary_damage_percentage?: number | null;
  is_final_grading_complete?: boolean | null;
  final_grading_expected_date?: string | null;
}

const QualityInfoBadge: React.FC<QualityInfoBadgeProps> = ({ procurement, compact = true }) => {
  const quality = procurement.quality as QualityData | undefined;

  if (!quality) {
    return compact ? null : (
      <span className="text-muted-foreground text-sm">No quality data</span>
    );
  }

  const isPreliminary = quality.grading_type === 'preliminary';
  const isFinalComplete = quality.is_final_grading_complete;
  
  // Get compensation values
  const preliminaryCompensation = quality.preliminary_compensation_kg || 0;
  const finalCompensation = quality.compensation_kg || 0;
  const displayCompensation = isPreliminary ? preliminaryCompensation : finalCompensation;
  
  // Check for reconciliation needed (preliminary exists and final differs)
  const hasReconciliationNeeded = isPreliminary && 
    quality.preliminary_compensation_kg !== null && 
    quality.compensation_kg !== null &&
    quality.preliminary_compensation_kg !== quality.compensation_kg;
  
  // Get damage percentage
  const damagePercentage = isPreliminary 
    ? quality.preliminary_damage_percentage 
    : quality.damage_percentage;

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1.5">
              {isPreliminary && !isFinalComplete ? (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                  <Clock className="h-3 w-3 mr-1" />
                  Est: {displayCompensation.toFixed(1)} kg
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  {finalCompensation.toFixed(1)} kg
                </Badge>
              )}
              
              {hasReconciliationNeeded && (
                <AlertTriangle className="h-4 w-4 text-amber-500" />
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="space-y-1 text-sm">
              <p className="font-medium">
                {isPreliminary ? 'Preliminary Assessment' : 'Final Grading'}
              </p>
              {damagePercentage !== null && damagePercentage !== undefined && (
                <p>Damage: {damagePercentage.toFixed(2)}%</p>
              )}
              <p>Compensation: {displayCompensation.toFixed(2)} kg</p>
              {hasReconciliationNeeded && (
                <p className="text-amber-600 font-medium">
                  ⚠ Reconciliation may be needed (Prelim: {preliminaryCompensation.toFixed(2)} kg vs Final: {finalCompensation.toFixed(2)} kg)
                </p>
              )}
              {isPreliminary && quality.final_grading_expected_date && (
                <p className="text-muted-foreground">
                  Final grading expected: {new Date(quality.final_grading_expected_date).toLocaleDateString()}
                </p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Expanded view
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {isPreliminary && !isFinalComplete ? (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
            <Clock className="h-3 w-3 mr-1" />
            Preliminary
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Final
          </Badge>
        )}
        
        {hasReconciliationNeeded && (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Reconciliation Needed
          </Badge>
        )}
      </div>
      
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-muted-foreground">Damage:</span>
          <span className="ml-2 font-medium">
            {damagePercentage !== null && damagePercentage !== undefined 
              ? `${damagePercentage.toFixed(2)}%` 
              : 'N/A'}
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">Compensation:</span>
          <span className="ml-2 font-medium">{displayCompensation.toFixed(2)} kg</span>
        </div>
      </div>
      
      {hasReconciliationNeeded && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
          <div className="flex items-center gap-2 text-amber-700 font-medium mb-1">
            <AlertTriangle className="h-4 w-4" />
            Reconciliation Alert
          </div>
          <p className="text-amber-600">
            Preliminary: {preliminaryCompensation.toFixed(2)} kg → Final: {finalCompensation.toFixed(2)} kg
            <br />
            Difference: {Math.abs(finalCompensation - preliminaryCompensation).toFixed(2)} kg
          </p>
        </div>
      )}
      
      {isPreliminary && quality.final_grading_expected_date && !isFinalComplete && (
        <p className="text-muted-foreground text-sm">
          Final grading expected: {new Date(quality.final_grading_expected_date).toLocaleDateString()}
        </p>
      )}
    </div>
  );
};

export default QualityInfoBadge;
